import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Button,
    Typography,
    TextField,
    Stepper,
    Step,
    StepLabel,
    Card,
    CardContent,
    Grid,
    Divider,
    Alert,
    InputAdornment,
    useTheme,
    useMediaQuery,
    styled,
    FormHelperText
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
    Home as HomeIcon,
    Description as DescriptionIcon,
    Send as SendIcon,
    Download as DownloadIcon,
    ArrowForward as ArrowForwardIcon,
    KeyboardArrowRight as KeyboardArrowRightIcon,
    Business as BusinessIcon,
    Apartment as ApartmentIcon,
    HelpOutline as HelpOutlineIcon,
    ErrorOutline as ErrorOutlineIcon,
    CheckCircleOutline as CheckCircleOutlineIcon
} from "@mui/icons-material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {ApiDetails, KEY} from "../../Constants";
import CryptoJS from "crypto-js";
import { handle401Unauthorized, handle403Forbidden } from '../../utils/authErrorHandler';


// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 12,
    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
    overflow: 'visible'
}));

const InfoBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main + '10', // Very light version of primary color
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    border: `1px solid ${theme.palette.primary.main}30`
}));

const PropertyField = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    }
}));

const DownloadFiles = (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    let data;
    let noOfProperties;
    let details = props.details;
    const [generate, setGenerate] = useState(false);
    const [inputs, setInputs] = useState(<div></div>);
    const [linkSet, setLinkSet] = useState(false);
    const [file, setFile] = useState("");
    const [showTitle, setShowTitle] = useState(true);
    const [hide, setHide] = useState(false);
    const [searchHistory] = useState(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [propertyNames, setPropertyNames] = useState([]);
    const [validationError, setValidationError] = useState("");
const [originalText, setOriginalText] = useState(() => {
    const encryptedData = localStorage.getItem("myMap");
    return encryptedData 
        ? CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8)
        : "";
});;

    const handleChange = (prop) => (event) => {
        searchHistory.set(event.target.name, event.target.value);

        // Update property names array for validation
        const index = parseInt(prop.replace('property', '')) - 1;
        const newPropertyNames = [...propertyNames];
        newPropertyNames[index] = event.target.value;
        setPropertyNames(newPropertyNames);
    };

    const next = () => {
        props.next();
    };

    // Form validation schema
    const validationSchema = Yup.object().shape({
        name: Yup.number()
            .typeError("Please enter a valid number")
            .positive("Must be a positive number")
            .integer("Must be a whole number")
            .required("Number of properties is required")
            .max(50, "Maximum 50 properties allowed")
    });

    // Initial form values
    const initialValues = {
        name: "",
    };

    // Handle property count submission
    const onSubmit = (values, formikProps) => {
        setIsLoading(true);

        // Parse property count
        noOfProperties = parseInt(values.name);

        // Initialize property names array with empty strings
        setPropertyNames(Array(noOfProperties).fill(""));

        // Generate input fields for each property
        setInputs(
            [...Array(noOfProperties)].map((_, index) => (
                <PropertyField key={index} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'row',
                    width: '100%'
                }}>
                    <ApartmentIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <TextField
                        required
                        fullWidth
                        id={`property-${index + 1}`}
                        label={`Property ${index + 1} Name`}
                        name={`property${index + 1}`}
                        placeholder={`e.g. Green Hills Apartment ${index + 1}`}
                        variant="outlined"
                        onChange={handleChange(`property${index + 1}`)}
                        error={validationError && !propertyNames[index]}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Typography color="text.secondary">{index + 1}.</Typography>
                                </InputAdornment>
                            ),
                            endAdornment: propertyNames[index] ? (
                                <InputAdornment position="end">
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                </InputAdornment>
                            ) : null
                        }}
                    />
                </PropertyField>
            ))
        );

        setGenerate(true);
        setShowTitle(false);
        setActiveStep(1);
        setIsLoading(false);
    };

    // Validate all property names are filled
    const validatePropertyNames = () => {
        // Check if any property name is empty
        const hasEmptyFields = propertyNames.some(name => !name || name.trim() === '');

        if (hasEmptyFields) {
            setValidationError("Please provide names for all properties");
            return false;
        }

        setValidationError("");
        return true;
    };

    // Convert base64 to blob for download
    function base64ToBlob(base64) {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; ++i) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        return new Blob([bytes], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
    }

    // Handle file download
    const download = () => {
        setIsLoading(true);

        // Create and trigger download
        const blob = base64ToBlob(file);
        const blobURL = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("download", `property_template.xlsx`);
        link.href = blobURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up
        setTimeout(() => {
            URL.revokeObjectURL(blobURL);
            setIsLoading(false);
            // Move to next page
            next();
        }, 1000);
    };

    // Generate download link
    const getDownloadLink = () => {
        // Validate property names before proceeding
        if (!validatePropertyNames()) {
            return;
        }

        setIsLoading(true);
        searchHistory.set("email_address", details.get("email_address"));

        fetch(ApiDetails + "pegasus/visionary/files/download", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            },
            body: JSON.stringify(Object.fromEntries(searchHistory)),
        })
            .then(async (response) => {
                data = await response.json();
                 // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                    setLinkSet(true);
                    setGenerate(false);
                    setShowTitle(false);
                    setHide(true);
                    setFile(data["File"]);
                    setActiveStep(2);
                    setIsLoading(false);
                } else if (response.status === 401) {
                    setIsLoading(false);
                    props.snackBar?.({
                        text: "Authentication failed. Please log in again.",
                        type: "error",
                    });
                } else if (response.status === 400) {
                    setIsLoading(false);
                    props.snackBar?.({
                        text: "Invalid data. Please check your property information.",
                        type: "error",
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                setIsLoading(false);
                props.snackBar?.({
                    text: "Something went wrong. Please try again.",
                    type: "error",
                });
            });
    };

    // Calculate completion percentage for property names
    const getCompletionStatus = () => {
        if (!propertyNames.length) return 0;
        const filledCount = propertyNames.filter(name => name && name.trim() !== '').length;
        return Math.round((filledCount / propertyNames.length) * 100);
    };

    // Steps for the property setup process
    const steps = ['Enter Property Count', 'Name Your Properties', 'Download Template'];

    return (
        <Box sx={{ maxWidth: '100%' }}>
            <StyledCard>
                <CardContent sx={{ p: isMobile ? 2 : 4 }}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" component="h2" fontWeight="bold" color="primary" gutterBottom>
                            Setup Your Properties
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Let's gather information about your properties to get your account ready
                        </Typography>
                    </Box>

                    {/* Progress stepper */}
                    <Stepper
                        activeStep={activeStep}
                        alternativeLabel={!isMobile}
                        sx={{ mb: 4 }}
                    >
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={onSubmit}
                    >
                        {(formikProps) => (
                            <Form noValidate>
                                {/* Step 1: Property Count */}
                                {showTitle && (
                                    <Box>
                                        <InfoBox>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                <BusinessIcon color="primary" sx={{ mt: 0.5, mr: 2 }} />
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                                                        Let's start with your property portfolio
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Please tell us how many properties you currently manage.
                                                        We'll create a customized template based on this information.
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </InfoBox>

                                        <Field name="name">
                                            {({ field, meta }) => (
                                                <TextField
                                                    {...field}
                                                    label="Number of Properties"
                                                    placeholder="e.g. 3"
                                                    variant="outlined"
                                                    fullWidth
                                                    type="number"
                                                    error={meta.touched && Boolean(meta.error)}
                                                    helperText={meta.touched && meta.error}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <HomeIcon color="primary" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{ mb: 3 }}
                                                />
                                            )}
                                        </Field>

                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                                            <LoadingButton
                                                type="submit"
                                                variant="contained"
                                                loading={isLoading}
                                                loadingPosition="end"
                                                endIcon={<KeyboardArrowRightIcon />}
                                                sx={{ px: 3, py: 1.2, borderRadius: 2 }}
                                            >
                                                Continue
                                            </LoadingButton>
                                        </Box>
                                    </Box>
                                )}

                                {/* Step 2: Property Names */}
                                {generate && !hide && (
                                    <Box>
                                        <InfoBox>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                <HelpOutlineIcon color="primary" sx={{ mt: 0.5, mr: 2 }} />
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                                                        Name each of your properties
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Please provide a unique name for each property so we can
                                                        identify them in your dashboard.
                                                        Be specific so you can easily recognize them later.
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </InfoBox>

                                        {/* Completion progress */}
                                        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {getCompletionStatus()}% Complete
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {propertyNames.filter(name => name && name.trim() !== '').length} of {propertyNames.length} properties named
                                            </Typography>
                                        </Box>

                                        {/* Property name fields */}
                                        <Box sx={{ mb: 3 }}>
                                            {inputs}
                                        </Box>

                                        {/* Validation error message */}
                                        {validationError && (
                                            <Alert
                                                severity="error"
                                                icon={<ErrorOutlineIcon />}
                                                sx={{ mb: 3, borderRadius: 2 }}
                                            >
                                                {validationError}
                                            </Alert>
                                        )}

                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                                            <LoadingButton
                                                variant="contained"
                                                onClick={getDownloadLink}
                                                loading={isLoading}
                                                loadingPosition="end"
                                                endIcon={<SendIcon />}
                                                sx={{ px: 3, py: 1.2, borderRadius: 2 }}
                                                disabled={getCompletionStatus() < 100}
                                            >
                                                Generate Template
                                            </LoadingButton>
                                        </Box>

                                        {getCompletionStatus() < 100 && (
                                            <FormHelperText sx={{ textAlign: 'right', mt: 1 }}>
                                                All property names are required to continue
                                            </FormHelperText>
                                        )}
                                    </Box>
                                )}

                                {/* Step 3: Download Instructions */}
                                {linkSet && (
                                    <Box>
                                        <Alert
                                            severity="info"
                                            variant="outlined"
                                            icon={<DescriptionIcon />}
                                            sx={{ mb: 3, borderRadius: 2 }}
                                        >
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Template Instructions
                                            </Typography>
                                            <Typography variant="body2">
                                                Please download and fill in the Excel template with your property
                                                details.
                                                The first sheet includes examples to guide you.
                                            </Typography>
                                        </Alert>

                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                bgcolor: "#f8f9fa",
                                                borderRadius: 2,
                                                mb: 3
                                            }}
                                        >
                                            <Typography variant="body2" paragraph>
                                                <strong>Important notes:</strong>
                                            </Typography>
                                            <ul style={{ paddingLeft: '20px', margin: '0' }}>
                                                <li>
                                                    <Typography variant="body2" paragraph>
                                                        Each sheet in the Excel file represents one of your properties
                                                    </Typography>
                                                </li>
                                                <li>
                                                    <Typography variant="body2" paragraph>
                                                        Fill in details for all units, even vacant ones
                                                    </Typography>
                                                </li>
                                                <li>
                                                    <Typography variant="body2" paragraph>
                                                        Be as accurate as possible with all information
                                                    </Typography>
                                                </li>
                                                <li>
                                                    <Typography variant="body2">
                                                        Save the file after completion before uploading it
                                                    </Typography>
                                                </li>
                                            </ul>
                                        </Paper>

                                        <Divider sx={{ my: 3 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                            <LoadingButton
                                                variant="contained"
                                                color="primary"
                                                onClick={download}
                                                loading={isLoading}
                                                loadingPosition="end"
                                                endIcon={<DownloadIcon />}
                                                size="large"
                                                sx={{
                                                    px: 4,
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    fontWeight: "bold"
                                                }}
                                            >
                                                Download Template
                                            </LoadingButton>
                                        </Box>
                                    </Box>
                                )}
                            </Form>
                        )}
                    </Formik>
                </CardContent>
            </StyledCard>
        </Box>
    );
};

export default DownloadFiles;